/**
 * Reactive variable store for the contract upload side drawer.
 *
 * Holds the open/closed state of the player contract drawer and the
 * currently selected player. Mode tracks whether the drawer is showing
 * the empty state ("no documents yet, click + Add Document") or the
 * upload section. Apollo `makeVar` is used to match the existing
 * frontend-v2 store pattern (see `features/global-search/stores/`).
 *
 * The drawer cannot be closed while an upload is in progress; the
 * `isUploading` flag is owned by the upload hook (Wave 3) and consulted
 * by the drawer container before honoring close requests.
 * @module features/contracts/stores/contractDrawerStore
 */

import { makeVar } from "@apollo/client";

import {
  CONTRACT_DRAWER_DEFAULT_DOCUMENT_TYPE,
  CONTRACT_DRAWER_DEFAULT_MODE,
} from "../constants";
import type { ContractDrawerState } from "../types";

/** Initial state for the contract drawer reactive variable. */
const INITIAL_STATE: ContractDrawerState = {
  open: false,
  playerId: null,
  playerName: null,
  documentType: CONTRACT_DRAWER_DEFAULT_DOCUMENT_TYPE,
  mode: CONTRACT_DRAWER_DEFAULT_MODE,
  selectedDocument: null,
  archived: false,
  tier: null,
  age: null,
};

/**
 * Apollo reactive variable holding the contract drawer state.
 * @remarks Components subscribe via `useReactiveVar(contractDrawerVar)`
 * (or via `useContractDrawerState`) and update via the helpers exported
 * below. State is intentionally a single object so consumers re-render
 * once per change rather than per field.
 */
export const contractDrawerVar = makeVar<ContractDrawerState>(INITIAL_STATE);

/**
 * Resets the contract drawer to its initial closed state.
 */
export function resetContractDrawer(): void {
  contractDrawerVar(INITIAL_STATE);
}
